import { useEffect, useId } from 'react'
import { useTranslation } from 'react-i18next'

import { useSamplePlayer } from '../../hooks/useSamplePlayer'
import { wordInfo } from '../../lib/words'

function PlayGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5z" />
    </svg>
  )
}

function PauseGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
    </svg>
  )
}

/**
 * The tap-a-word sheet (V2-w): what the learner sees after tapping one
 * word of the sentence. With a shared-dictionary entry (`sentence.words`,
 * V2-b) it plays the word's pronunciation and shows the meaning,
 * translation, explanation and examples; with only an old admin note it
 * shows that note; with neither it says so — every word answers something.
 *
 * A bottom sheet rather than the P-alerts `TootiDialog`: this is content to
 * read and listen to, not a question to confirm. Escape closes it; the
 * player stops with it (the hook pauses on unmount).
 */
export default function WordSheet({ word, sentence, index, onClose }) {
  const { t } = useTranslation('lesson')
  const titleId = useId()
  const player = useSamplePlayer()
  const info = wordInfo(sentence, index)

  useEffect(() => {
    function onKey(event) {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const entry = info.kind === 'entry' ? info.entry : null
  const playing = entry && player.playingId === entry.id && player.isPlaying
  const details = entry
    ? [
        ['meaning', entry.meaningFa],
        ['translation', entry.translationFa],
        ['explanation', entry.explanationFa],
      ].filter(([, value]) => value)
    : []

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-[rgba(8,15,24,0.55)]" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-word-sheet={info.kind}
        onClick={(event) => event.stopPropagation()}
        className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-t-[22px] bg-surface px-6 pb-8 pt-4 shadow-[0_-12px_40px_rgba(0,0,0,0.3)]"
      >
        <span aria-hidden="true" className="mx-auto mb-4 block h-1 w-10 rounded-full bg-line" />

        <div className="flex items-center justify-between gap-3">
          <h2 id={titleId} dir="ltr" className="lesson-text text-left text-xl font-bold text-ink">
            {entry?.headword ?? word}
          </h2>
          {entry?.audioUrl && (
            <button
              type="button"
              data-testid="word-play"
              aria-pressed={Boolean(playing)}
              onClick={() => player.toggle(entry.id, entry.audioUrl)}
              className="flex h-10 shrink-0 items-center gap-2 rounded-xl bg-brand-600 px-4 text-[13px] font-bold text-white transition hover:bg-brand-700 dark:text-slate-900"
            >
              {playing ? <PauseGlyph /> : <PlayGlyph />}
              <span>{playing ? t('wordSheet.pause') : t('wordSheet.play')}</span>
            </button>
          )}
        </div>
        {player.error && <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{t('wordSheet.playFailed')}</p>}

        {info.kind === 'entry' && (
          <dl className="mt-4 space-y-3">
            {details.map(([key, value]) => (
              <div key={key}>
                <dt className="text-[11px] font-semibold text-ink-3">{t(`wordSheet.${key}`)}</dt>
                <dd className="mt-0.5 text-[15px] leading-7 text-ink">{value}</dd>
              </div>
            ))}
            {details.length === 0 && <p className="text-sm text-ink-3">{t('wordSheet.nothingYet')}</p>}
            {entry.examples.length > 0 && (
              <div>
                <dt className="text-[11px] font-semibold text-ink-3">{t('wordSheet.examples')}</dt>
                <dd>
                  <ul className="mt-1 space-y-2">
                    {entry.examples.map((example, i) => (
                      <li key={i} className="rounded-xl bg-surface-2 px-3 py-2">
                        <p dir="ltr" className="lesson-text text-left text-[15px] text-ink">{example.textEn}</p>
                        <p className="mt-0.5 text-[13px] text-ink-2">{example.textFa}</p>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            )}
          </dl>
        )}

        {info.kind === 'note' && <p className="mt-4 text-[15px] leading-7 text-ink">{info.note}</p>}
        {info.kind === 'none' && <p className="mt-4 text-sm text-ink-3">{t('wordSheet.nothingYet')}</p>}

        <button
          type="button"
          onClick={onClose}
          className="mt-6 h-11 w-full rounded-xl bg-surface-2 text-[13px] font-bold text-ink transition hover:bg-surface-3"
        >
          {t('wordSheet.close')}
        </button>
      </div>
    </div>
  )
}
